import {computed, inject, Injectable, signal} from '@angular/core';
import {environment} from '@epg-apps/cdh-environments';
import {HttpClient} from '@angular/common/http';
import {
  DetailedEnvironment,
  Environment,
  EnvironmentState,
  INIT_DETAILED_ENVIRONMENT_STATE,
  INIT_ENVIRONMENTS_STATE,
  PageDto,
} from '@epg-apps/cdh-models';
import {Observable, take} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService {
  private static readonly ENVIRONMENT_CDH_API =
    environment.cdhApi + 'environments';
  private readonly http = inject(HttpClient);

  private readonly environmentState = signal<EnvironmentState>({
    environments: INIT_ENVIRONMENTS_STATE,
    isLoadingPage: false,
    detailedEnvironment: INIT_DETAILED_ENVIRONMENT_STATE,
    showAll: false,
  });

  selectedUserEnvironments = computed(
    () => this.environmentState().environments.content,
  );
  mostRecentPageable = computed(
    () => this.environmentState().environments.pageable,
  );
  detailedEnvironment = computed(
    () => this.environmentState().detailedEnvironment,
  );
  showAll = computed(() => this.environmentState().showAll);

  public loadEnvironmentsPage(
    customerId = '',
    pageNumber = 0,
    pageSize = 10,
    showAll = false,
  ): void {
    this.getEnvironmentsPage(customerId, pageNumber, pageSize, showAll)
      .pipe(take(1))
      .subscribe((customersPage) => {
        this.setEnvironmentPage(customersPage);
      });
  }

  public getEnvironmentsPage(
    customerId: string,
    pageNumber: number,
    pageSize: number,
    showAll: boolean,
  ): Observable<PageDto<Environment>> {
    const headerOptions = {
      params: {
        customerId: customerId,
        pageNumber: pageNumber,
        pageSize: pageSize,
        status: showAll ? ['ACTIVE', 'HIDDEN', 'INVALID'] : ['ACTIVE'],
      },
    };
    this.setIsLoading();
    return this.http.get<PageDto<Environment>>(
      EnvironmentService.ENVIRONMENT_CDH_API,
      headerOptions,
    );
  }

  public setEnvironmentPage(environmentsPage: PageDto<Environment>): void {
    this.environmentState.update((state): EnvironmentState => {
      return {
        ...state,
        isLoadingPage: false,
        environments: environmentsPage,
      };
    });
  }

  public getDetailedEnvironmentById(
    id: string,
  ): Observable<DetailedEnvironment> {
    const environmentApiUrl = EnvironmentService.ENVIRONMENT_CDH_API + '/' + id;
    return this.http.get<DetailedEnvironment>(environmentApiUrl);
  }

  public setDetailedEnvironment(
    detailedEnvironmentUI: DetailedEnvironment,
  ): void {
    this.environmentState.update((state) => ({
      ...state,
      detailedEnvironment: detailedEnvironmentUI,
    }));
  }

  private setIsLoading(): void {
    this.environmentState.update((state) => {
      return {
        ...state,
        isLoadingPage: true,
      };
    });
  }

  public setShowAll(showAll: boolean): void {
    this.environmentState.update((state) => ({
      ...state,
      showAll: showAll,
    }));
  }

  public hideEnvironment(environmentId: string): Observable<void> {
    const environmentApiUrl = `${EnvironmentService.ENVIRONMENT_CDH_API}/${environmentId}/hide`;
    return this.http.post<void>(environmentApiUrl, {});
  }

  public showEnvironment(environmentId: string): Observable<void> {
    const environmentApiUrl = `${EnvironmentService.ENVIRONMENT_CDH_API}/${environmentId}/unhide`;
    return this.http.post<void>(environmentApiUrl, {});
  }
}
